import React from 'react';
import { DroneRegisterFormProps } from './types';
import { Controller, useForm } from 'react-hook-form';
import { IDroneRegisterForm } from '@src/features/fleet/entities';
import { EMPTY_VALUE, REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { ArcButton, ArcDialog, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcStack, ArcText } from '@src/core/ui';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setSelectedDrone } from '@src/features/fleet/pages/drones-page/redux';
import { useRegisterDroneMutation } from '@src/features/fleet/redux/api';
import { useGetHubsQuery } from '@src/features/hubs/redux/api';
import { trackError, toTrackedError } from '@src/core/sentry';

const DroneRegisterForm: React.FC<DroneRegisterFormProps> = (props) => {
  const { drone, open, title, onClose } = props;

  const dispatch = useDispatch();
  const [registerDrone, { isLoading }] = useRegisterDroneMutation();
  const { data: hubs = [] } = useGetHubsQuery();

  const currentHubId = drone?.hubId ?? drone?.hub?.id ?? null;
  const currentHubName = drone?.hubName ?? drone?.hub?.name ?? null;

  const hubOptions = React.useMemo(() => {
    const options = hubs.map((hub) => ({ id: hub.id, name: hub.name }));
    if (currentHubId != null && !options.some((o) => o.id === currentHubId)) {
      options.unshift({ id: currentHubId, name: currentHubName ?? `Hub #${currentHubId}` });
    }
    return options;
  }, [hubs, currentHubId, currentHubName]);

  const { control, register, reset, handleSubmit, formState: { errors } } = useForm<IDroneRegisterForm>({
    defaultValues: {
      name: drone?.name ?? EMPTY_VALUE,
      hubId: currentHubId ?? '',
    }
  });

  const onRegisterDrone = (form: IDroneRegisterForm) => {
    if (!drone?.id) return;

    registerDrone({ id: drone.id, name: form.name, hubId: form.hubId === '' ? null : Number(form.hubId) }).unwrap()
      .then((response) => {
        toast.success('Register Drone Success Text');
        dispatch(setSelectedDrone({
          ...drone,
          ...response,
          hubId: form.hubId === '' ? null : Number(form.hubId),
        }));
        onClose();
      })
      .catch((err: unknown) => {
        trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'registerDrone' } });
        toast.error('Failed to register drone');
      });
  };

  React.useEffect(() => {
    reset({
      name: drone?.name ?? EMPTY_VALUE,
      hubId: currentHubId ?? '',
    });
  }, [drone, currentHubId, reset]);

  return (
    <ArcDialog open={open} onClose={onClose} title={title}>
      <ArcStack component='form' spacing={2} onSubmit={handleSubmit(onRegisterDrone)}>

        <ArcText.h5 fontSize={10}>DETAILS </ArcText.h5>
        <ArcOutlinedInput 
          autoComplete='off' 
          label='Drone Name'
          error={Boolean(errors.name)} 
          helperText={errors.name?.message}
          {...register('name', {
            required: REQUIRED_VALIDATION_TEXT
          })} />

        <ArcText.h5 fontSize={10}>HUB </ArcText.h5>

        <Controller
          control={control}
          name='hubId'
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field }) => (
            <ArcOutlinedSelect
              {...field}
              label='Assigned HUB'
              error={Boolean(errors.hubId)}
              helperText={errors.hubId?.message}>
              {hubOptions.map((hub) => (
                <ArcMenuItem key={hub.id} value={hub.id}>
                  {hub.name}
                </ArcMenuItem>
              ))}
            </ArcOutlinedSelect>
          )} />

        <ArcButton.brand 
          size='large' 
          type='submit' 
          isLoading={isLoading}>
            Save
        </ArcButton.brand>
      </ArcStack>

      
    </ArcDialog>
  );
};

export default DroneRegisterForm;
